/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PhoneInput, {
  isPossiblePhoneNumber,
  getCountryCallingCode,
} from 'react-phone-number-input'
import type { Country } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { paragraphSmall } from 'src/components/css'

interface ModernPhoneInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  error?: string
  label: string
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Enable phone number validation on blur */
  validateOnBlur?: boolean
  /** Callback when validation state changes */
  onValidationChange?: (isValid: boolean) => void
  /** Style variant: 'modern' (default) or 'legacy' (matches FormInput) */
  variant?: 'modern' | 'legacy'
  /** Callback when country changes - receives ISO 3166-1 alpha-2 code (e.g., 'US', 'CA') */
  onCountryChange?: (country: string | undefined) => void
  /** Hide error label */
  hideErrorLabel?: boolean
}

const containerStyles = css`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex: 1;
  gap: 8px;
`

const labelStyles = css`
  color: var(--Neutrals-neutrals-0, #fff);
  font-feature-settings: 'cv03' on, 'cv04' on;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 145%;
`

const phoneInputWrapperStyles = css`
  display: flex;
  padding: 8px 8px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid var(--Foundation-Secondary-secondary-100, #4e5561);
  background: var(--Foundation-Secondary-secondary-200, #323a48);
  height: 40px;
  transition: border-color 0.3s, background-color 0.3s;

  .PhoneInput {
    width: 100%;
    display: flex;
    align-items: center;
  }

  .PhoneInputInput {
    color: var(--Neutrals-neutrals-0, #fff);
    font-feature-settings: 'cv01' on, 'cv03' on, 'cv04' on;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 145%;
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    height: 36px !important;

    &::placeholder {
      color: #c1c2c3;
    }
  }

  .PhoneInputCountry {
    margin-right: 8px;
    display: flex;
    align-items: center;
  }

  .PhoneInputCountrySelect {
    ${paragraphSmall}
    background: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    padding: 0;
    margin-right: 4px;
  }

  .PhoneInputCountryIcon {
    width: 20px;
    height: 15px;
    margin-right: 4px;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
  }

  .PhoneInputCountrySelectArrow {
    color: #c1c2c3;
    opacity: 0.6;
    width: 0.3em;
    height: 0.3em;
    margin-left: 4px;
    border-style: solid;
    border-color: currentColor;
    border-top-width: 0;
    border-bottom-width: 1px;
    border-left-width: 0;
    border-right-width: 1px;
    transform: rotate(45deg);
  }
`

const focusedInputStyles = css`
  border-color: #f2b400;
  background-color: #27303e;
`

const errorStyles = css`
  font-size: 13px;
  line-height: 16px;
  color: ${colors.ERROR_LIGHT};
  text-transform: capitalize;
`

const disabledStyles = css`
  opacity: 0.5;
  pointer-events: none;
`

// Legacy styles (matches FormInput component styling)
const legacyContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex: 1;
`

const legacyLabelStyles = css`
  color: var(--Neutrals-neutrals-0, #fff);
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 145%;
  text-transform: capitalize;
  margin-bottom: 4px;
`

const legacyPhoneInputWrapperStyles = css`
  display: flex;
  padding: 0 12px;
  align-items: center;
  align-self: stretch;
  border-radius: 2px;
  border: 1px solid;
  background: transparent;
  height: 46px;
  transition: border-color 0.3s;

  .PhoneInput {
    width: 100%;
    display: flex;
    align-items: center;
  }

  .PhoneInputInput {
    color: var(--Neutrals-neutrals-0, #fff);
    font-family: Inter, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: 0.74px;
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    height: 44px !important;

    &::placeholder {
      opacity: 0.5;
    }
  }

  .PhoneInputCountry {
    margin-right: 8px;
    display: flex;
    align-items: center;
  }

  .PhoneInputCountrySelect {
    ${paragraphSmall}
    border: none;
    cursor: pointer;
    outline: none;
    padding: 0;
    margin-right: 4px;
  }

  .PhoneInputCountryIcon {
    width: 20px;
    height: 15px;
    margin-right: 4px;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
  }

  .PhoneInputCountrySelectArrow {
    color: #c1c2c3;
    opacity: 0.6;
    width: 0.3em;
    height: 0.3em;
    margin-left: 4px;
    border-style: solid;
    border-color: currentColor;
    border-top-width: 0;
    border-bottom-width: 1px;
    border-left-width: 0;
    border-right-width: 1px;
    transform: rotate(45deg);
  }
`

const legacyFocusedInputStyles = css`
  border-color: #ffffff;
`

/**
 * Detects the user's country based on browser locale
 * Falls back to 'US' if detection fails
 */
const detectUserCountry = (): Country => {
  try {
    // Try to get country from browser's Intl API
    const locale = navigator.language || 'en-US'
    const region = new Intl.Locale(locale).region
    return (region as Country) || 'US'
  } catch (error) {
    console.warn(
      '⚠️ detectUserCountry: Failed to detect country, using US',
      error
    )
    return 'US'
  }
}

/**
 * Custom input component for PhoneInput
 * Extracted to avoid re-creating on every render
 */
interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onFocusChange?: (focused: boolean) => void
  customTabIndex?: number
  /** Additional ref to track the raw input value */
  inputRef?: React.RefObject<HTMLInputElement>
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  ({ onFocusChange, customTabIndex, inputRef, ...props }, ref) => {
    // Combine the library's ref with our inputRef
    const combinedRef = useCallback(
      (node: HTMLInputElement | null) => {
        // Set the library's ref
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
        // Set our inputRef
        if (inputRef) {
          ;(
            inputRef as React.MutableRefObject<HTMLInputElement | null>
          ).current = node
        }
      },
      [ref, inputRef]
    )

    return (
      <input
        {...props}
        ref={combinedRef}
        onFocus={(e) => {
          onFocusChange?.(true)
          props.onFocus?.(e)
        }}
        onBlur={(e) => {
          onFocusChange?.(false)
          props.onBlur?.(e)
        }}
        autoComplete='tel'
        tabIndex={customTabIndex}
      />
    )
  }
)

/**
 * Modern phone input component with international support
 * Uses react-phone-number-input library for validation and formatting
 * Drop-in replacement for existing PhoneInput component
 */
const ModernPhoneInput: React.FC<ModernPhoneInputProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  label,
  tabIndex,
  validateOnBlur = true,
  onValidationChange,
  variant = 'modern',
  onCountryChange,
  hideErrorLabel = false,
}) => {
  const isLegacy = variant === 'legacy'

  const [isFocused, setIsFocused] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Refs for tracking raw input value and current country
  const inputRef = useRef<HTMLInputElement>(null)
  const currentCountryRef = useRef<Country | undefined>(undefined)

  // Detect default country only once on mount
  const defaultCountry = useMemo(() => {
    const detected = detectUserCountry()
    return detected
  }, [])

  // Initialize currentCountryRef with defaultCountry on mount
  useEffect(() => {
    if (defaultCountry) {
      currentCountryRef.current = defaultCountry
    }
  }, [defaultCountry])

  // Call onCountryChange on mount with the default country
  // Only send if there's no existing value - if there's a value, the library
  // will detect the country from the phone number and call onCountryChange
  useEffect(() => {
    if (onCountryChange && defaultCountry && !value) {
      onCountryChange(defaultCountry)
    }
  }, []) // Only run on mount

  /**
   * Handles country selection changes from the PhoneInput component
   * Notifies parent via callback and updates the ref
   */
  const handleCountryChange = useCallback(
    (country: Country | undefined) => {
      currentCountryRef.current = country
      onCountryChange?.(country)
    },
    [onCountryChange]
  )

  /**
   * Validates the phone number using isPossiblePhoneNumber
   * Returns true if valid or empty, false otherwise
   */
  const validatePhoneNumber = useCallback(
    (phoneValue: string): boolean => {
      // Empty value is considered valid (not required validation)
      if (!phoneValue || phoneValue.trim() === '') {
        setValidationError(null)
        onValidationChange?.(true)
        return true
      }

      if (!validateOnBlur) {
        return true
      }

      const isValid = isPossiblePhoneNumber(phoneValue)

      if (!isValid) {
        setValidationError('Please enter a valid phone number')
        onValidationChange?.(false)
        return false
      }

      setValidationError(null)
      onValidationChange?.(true)
      return true
    },
    [onValidationChange, validateOnBlur]
  )

  const handleFocusChange = useCallback(
    (focused: boolean) => {
      setIsFocused(focused)

      // Validate on blur if enabled
      if (!focused && validateOnBlur && value) {
        validatePhoneNumber(value)
      }
    },
    [validateOnBlur, value, validatePhoneNumber]
  )

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      // Clear validation error when user starts typing
      if (validationError) {
        setValidationError(null)
      }

      // If library returns undefined but user typed something,
      // construct value with country calling code + raw input
      if (newValue === undefined && inputRef.current?.value) {
        const rawValue = inputRef.current.value.replace(/\D/g, '') // digits only
        const country = currentCountryRef.current
        if (country && rawValue) {
          try {
            const callingCode = getCountryCallingCode(country)
            onChange(`+${callingCode}${rawValue}`)
            return
          } catch {
            // If getCountryCallingCode fails, fall through to default behavior
          }
        }
      }

      // Pass the value from the library (valid E.164 format or empty)
      onChange(newValue || '')
    },
    [onChange, validationError]
  )

  // Display external error or internal validation error
  const displayError = error || validationError

  return (
    <div css={isLegacy ? legacyContainerStyles : containerStyles}>
      <label css={isLegacy ? legacyLabelStyles : labelStyles}>{label}</label>
      <div
        css={[
          isLegacy ? legacyPhoneInputWrapperStyles : phoneInputWrapperStyles,
          isFocused &&
            (isLegacy ? legacyFocusedInputStyles : focusedInputStyles),
          disabled && disabledStyles,
          displayError &&
            css`
              border-color: ${colors.ERROR_LIGHT};
            `,
        ]}
      >
        <PhoneInput
          international={false}
          initialValueFormat='national'
          defaultCountry={defaultCountry}
          value={value}
          onChange={handleChange}
          onCountryChange={handleCountryChange}
          disabled={disabled}
          placeholder='(234) 567 8901'
          inputComponent={CustomInput}
          inputProps={{
            onFocusChange: handleFocusChange,
            tabIndex,
            inputRef: inputRef,
          }}
          onBlur={() => {
            validatePhoneNumber(value)
          }}
          countrySelectProps={{
            title: '', // Remove hover tooltip
            tabIndex,
          }}
          limitMaxLength={true}
        />
      </div>
      {!hideErrorLabel && displayError && (
        <div css={errorStyles}>{displayError}</div>
      )}
    </div>
  )
}

export default ModernPhoneInput

/**
 * Re-export isPossiblePhoneNumber for external validation use cases
 * Can be used to validate phone numbers before form submission
 */
export { isPossiblePhoneNumber }
